import React, { useState, useCallback, useRef, useEffect } from 'react';
import 'antd/dist/antd.css';
import { Popconfirm, Space, Spin, Switch, notification } from 'antd';
import { DndProvider, useDrag, useDrop, createDndContext } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import {
  getTrainerPublicProfilesInOrder,
  updateTrainerPublicProfileInfo,
  updateTrainerPublicProfileOrder,
} from '../../services/trainerServices';
import { Link } from 'react-router-dom';
import { sortableHandle } from 'react-sortable-hoc';
import { MenuOutlined } from '@ant-design/icons';
import TableWithSorter from '../../components/Common/TableWithSorter';

const RNDContext = createDndContext(HTML5Backend);

const type = 'DragableBodyRow';

const DragableBodyRow = ({
  index,
  moveRow,
  className,
  style,
  ...restProps
}) => {
  const ref = React.useRef();
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName:
          dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
      };
    },
    drop: (item) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    item: { type, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));
  return (
    <tr
      ref={ref}
      className={`${className}${isOver ? dropClassName : ''}`}
      style={{ cursor: 'move', ...style }}
      {...restProps}
    />
  );
};

const TrainerPublicProfile = () => {
  const [trainers, setTrainers] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(false);
  const [recordCount, setRecordCount] = useState(false);

  const components = {
    body: {
      row: DragableBodyRow,
    },
  };

  const moveRow = useCallback(
    (dragIndex, hoverIndex) => {
      const dragRow = trainers[dragIndex];
      const updatedData = update(trainers, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRow],
        ],
      });
      const newTrainerOrders = updatedData.map((trainer) => trainer._id);
      updateTrainerPublicProfileOrder({
        trainerOrdering: newTrainerOrders,
      }).then((r) => {
        if (!r.error) {
          openNotificationWithIcon(
            'success',
            'Order is updated!',
            'Trainers order is updated'
          );
          setTrainers(updatedData);
        }
      });
    },
    [trainers]
  );

  const manager = useRef(RNDContext);

  const getData = async () => {
    setFetching(true);
    await getTrainerPublicProfilesInOrder({}).then((response) => {
      if (!response.error) {
        const sortedTrainers = response.data?.trainerOrdering;
        setRecordCount(sortedTrainers.length);
        setTrainers(sortedTrainers);
      } else {
        setError(error);
      }
    });
    setFetching(false);
  };

  useEffect(() => {
    getData().then();
  }, []);

  const changeProfileStatus = async (
    id,
    firstName,
    statusObject,
    newStatus
  ) => {
    await updateTrainerPublicProfileInfo(id, statusObject)
      .then((response) => {
        getData().then();
        openNotificationWithIcon(
          'success',
          'Status changed',
          `Trainer ${firstName} is now ${
            newStatus ? '' : 'NOT'
          } listed on website`
        );
      })
      .catch((error) => {
        openNotificationWithIcon(
          'error',
          'Status change failed!',
          'Posting data to the backend failed!'
        );
      });
  };

  const openNotificationWithIcon = (type, title, msg) => {
    notification[type]({
      message: title,
      description: msg,
    });
  };

  const DragHandle = sortableHandle(() => (
    <MenuOutlined style={{ cursor: 'pointer', color: '#999' }} />
  ));

  const columns = [
    {
      title: 'Sort',
      dataIndex: 'sort',
      width: 30,
      className: 'drag-visible',
      render: () => <DragHandle />,
    },
    {
      title: 'Trainer',
      dataIndex: 'trainerData',
      key: 'trainerData',
      render: (value) => (
        <Space size="middle">
          <Link to={'/trainer/' + value?.trainerId}>{value?.trainerId}</Link>
        </Space>
      ),
    },
    {
      title: 'First Name',
      dataIndex: 'trainerData',
      key: 'trainerData',
      render: (value) => <span>{value?.firstName}</span>,
    },
    {
      title: 'Last Name',
      dataIndex: 'trainerData',
      key: 'trainerData',
      render: (value) => <span>{value?.lastName}</span>,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Listed on website?',
      dataIndex: 'isPublic',
      key: 'isPublic',
      render: (text, record) => (
        <Popconfirm
          placement="top"
          title="Are you sure you want to change status?"
          onConfirm={() =>
            changeProfileStatus(
              record?.trainerObjectId,
              record?.trainerData?.firstName,
              { isPublic: !text },
              !text
            )
          }
          okText="Yes"
          cancelText="No"
        >
          <Switch checked={text} />
        </Popconfirm>
      ),
    },
    {
      title: 'Show individual on website?',
      dataIndex: 'isShowIndividualPage',
      key: 'isShowIndividualPage',
      render: (text, record) => (
        <Popconfirm
          placement="top"
          title="Are you sure you want to change status?"
          onConfirm={() =>
            changeProfileStatus(
              record?.trainerObjectId,
              record?.trainerData?.firstName,
              { isShowIndividualPage: !text },
              !text
            )
          }
          okText="Yes"
          cancelText="No"
        >
          <Switch checked={text} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <DndProvider manager={manager.current.dragDropManager}>
      {fetching ? <Spin /> : null}
      {trainers && trainers.length > 0 ? (
        <div>
          <h1>Trainer Public Profile Listing</h1>
          <p>
            Order how you want the trainers to be seen on
            <Link
              to={{ pathname: 'https://nas.academy/trainers' }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {' '}
              website
            </Link>
          </p>
          <p> To search for trainers, use Cmd + F (Mac) Ctrl + F (Windows) </p>
          <TableWithSorter
            columns={columns}
            dataSource={trainers}
            components={components}
            onRow={(record, index) => ({
              index,
              moveRow,
            })}
            pagination={{
              total: recordCount,
              pageSize: recordCount,
              hideOnSinglePage: true,
            }}
          />
        </div>
      ) : null}
    </DndProvider>
  );
};

export default TrainerPublicProfile;
